import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, EmptyState } from "../components/shared";
import { formatMoney, CURRENCIES } from "../lib/constants";
import { ContactCombobox } from "../components/ContactCombobox";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Plus, Trash2, Check, X, Clock } from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  pending: { label: "Pending", cls: "bg-amber-400 text-black", icon: Clock },
  approved: { label: "Approved", cls: "bg-success text-white", icon: Check },
  rejected: { label: "Rejected", cls: "bg-destructive text-white", icon: X },
};

export default function Expenses() {
  const { user } = useAuth();
  const canApprove = ["admin", "accountant"].includes(user.role);
  const base = user.organization?.base_currency || "NGN";
  const empty = { category: "", amount: 0, currency: base, date: new Date().toISOString().slice(0, 10), note: "", receiver_id: null };
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [rejecting, setRejecting] = useState(null);
  const [reason, setReason] = useState("");

  const load = useCallback(async () => setItems((await api.get("/expenses")).data), []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    try { await api.post("/expenses", form); toast.success("Expense submitted for approval"); setOpen(false); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const decide = async (id, action, why = "") => {
    try { await api.put(`/expenses/${id}/decision`, { action, reason: why }); toast.success(`Expense ${action}d`); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const submitReject = async () => {
    if (!reason.trim()) { toast.error("Please give a reason"); return; }
    await decide(rejecting, "reject", reason); setRejecting(null); setReason("");
  };
  const del = async (id) => { if (!window.confirm("Delete expense?")) return; await api.delete(`/expenses/${id}`); toast.success("Deleted"); load(); };

  return (
    <div data-testid="expenses-page">
      <PageHeader title="Expenses" subtitle="Spending requests & approvals"
        action={<Button className="rounded-none gap-2" data-testid="create-expense-btn"
          onClick={() => { setForm(empty); setOpen(true); }}>
          <Plus className="w-4 h-4" /> New Expense</Button>} />

      {items.length === 0 ? <EmptyState title="No expenses recorded" /> : (
        <div className="border border-border bg-card overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-secondary">
              <TableHead>Date</TableHead><TableHead>Category</TableHead><TableHead>Receiver</TableHead>
              <TableHead>Submitted by</TableHead><TableHead>Status</TableHead><TableHead>Approved by</TableHead>
              <TableHead className="text-right">Amount</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {items.map((x) => {
                const st = STATUS[x.status] || STATUS.pending;
                const Icon = st.icon;
                return (
                  <TableRow key={x.id} className="hover:bg-secondary/60" data-testid="expense-row">
                    <TableCell className="font-mono text-sm">{x.date}</TableCell>
                    <TableCell className="font-medium">{x.category}<div className="text-xs text-muted-foreground">{x.note}</div></TableCell>
                    <TableCell className="text-sm">{x.receiver_name || "—"}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{x.submitted_by_name || "—"}</TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center gap-1 text-xs font-mono font-semibold uppercase px-2 py-0.5 ${st.cls}`} data-testid={`expense-status-${x.id}`}>
                        <Icon className="w-3 h-3" />{st.label}
                      </span>
                      {x.status === "rejected" && x.decision_reason && <div className="text-xs text-destructive mt-1" data-testid="expense-reject-reason">Reason: {x.decision_reason}</div>}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{x.approver_name || "—"}</TableCell>
                    <TableCell className="text-right font-mono">{formatMoney(x.amount, x.currency)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {canApprove && x.status === "pending" && (
                          <>
                            <Button size="sm" variant="outline" className="h-8 rounded-none gap-1 text-success border-success" onClick={() => decide(x.id, "approve")} data-testid={`approve-expense-${x.id}`}><Check className="w-3.5 h-3.5" />Approve</Button>
                            <Button size="sm" variant="outline" className="h-8 rounded-none gap-1 text-destructive border-destructive" onClick={() => { setRejecting(x.id); setReason(""); }} data-testid={`reject-expense-${x.id}`}><X className="w-3.5 h-3.5" />Reject</Button>
                          </>
                        )}
                        {canApprove && <Button size="icon" variant="ghost" className="h-8 w-8 rounded-none text-destructive" onClick={() => del(x.id)} data-testid={`delete-expense-${x.id}`}><Trash2 className="w-4 h-4" /></Button>}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none">
          <DialogHeader><DialogTitle className="font-display">New Expense Request</DialogTitle><DialogDescription>Submit a spending request. It needs Accountant or Admin approval.</DialogDescription></DialogHeader>
          <form onSubmit={save} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2"><Label>Category</Label><Input className="rounded-none mt-1" value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} required placeholder="Rent, Utilities…" data-testid="expense-category-input" /></div>
            <div className="sm:col-span-2"><Label>Receiver (person / vendor)</Label>
              <ContactCombobox type="supplier" value={form.receiver_id} onValueChange={(id) => setForm((f) => ({ ...f, receiver_id: id }))} placeholder="Search or add a vendor…" testId="expense-receiver" />
            </div>
            <div><Label>Amount</Label><Input type="number" step="any" className="rounded-none mt-1" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: parseFloat(e.target.value || 0) }))} data-testid="expense-amount-input" /></div>
            <div><Label>Currency</Label>
              <Select value={form.currency} onValueChange={(v) => setForm((f) => ({ ...f, currency: v }))}>
                <SelectTrigger className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c.code} value={c.code}>{c.symbol} {c.code}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Date</Label><Input type="date" className="rounded-none mt-1" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} /></div>
            <div><Label>Note</Label><Input className="rounded-none mt-1" value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} /></div>
            <DialogFooter className="sm:col-span-2"><Button type="submit" className="rounded-none" data-testid="save-expense-btn">Submit for Approval</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!rejecting} onOpenChange={(v) => !v && setRejecting(null)}>
        <DialogContent className="rounded-none">
          <DialogHeader><DialogTitle className="font-display">Reject Expense</DialogTitle><DialogDescription>Provide a reason. The submitter and admins will see it.</DialogDescription></DialogHeader>
          <div><Label>Reason</Label><Input className="rounded-none mt-1" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Not budgeted this month" data-testid="reject-reason-input" /></div>
          <DialogFooter><Button variant="destructive" className="rounded-none" onClick={submitReject} data-testid="confirm-reject-btn">Reject Expense</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
